import { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
} from '@mui/material';
import { Block as BlockIcon, Logout as LogoutIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthProvider';

/**
 * AccessDenied
 *
 * Full-page screen shown when an authenticated user has no app-level
 * permissions. Renders a localized title/message and a single Sign Out
 * action that calls `useAuth().logout()`.
 *
 * Used by Load and Publish/Review for zero-permission users.
 */
export function AccessDenied() {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await auth.logout();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <BlockIcon color="error" sx={{ fontSize: 80, mb: 2 }} />

          <Typography variant="h4" component="h1" gutterBottom color="error">
            {t('accessDenied.title')}
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            paragraph
            sx={{ mb: 3 }}
          >
            {t('accessDenied.message')}
          </Typography>

          <Button
            variant="contained"
            color="primary"
            startIcon={<LogoutIcon />}
            onClick={handleSignOut}
            disabled={signingOut}
            size="large"
          >
            {t('accessDenied.signOut')}
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
